import { Injectable } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';

import { AbstractDataStore } from 'v2/data-store/abstract.data-store';
import { State } from 'v2/data-store/state';

import { ECostLayerRelation } from '../cost-layers.const';
import { ICostLayer, ICostLayerEditFields } from '../cost-layers.types';

import { CostLayersApiService } from './cost-layers-api.service';

interface ICostLayerEditState {
  computedTotalCost :string | number;
  costLayer :ICostLayer;
  costLayerLoading :boolean;
  costLayerSaving :boolean;
}

export interface ICostLayerEditDataStoreLoadOptions {
  relations ?:ECostLayerRelation[];
}
export interface ICostLayerEditDataStoreSaveOptions {}

@Injectable()
export class CostLayerEditDataStore extends AbstractDataStore<
  ICostLayerEditState,
  State<ICostLayerEditState>
> {
  readonly state = new State<ICostLayerEditState>({
    computedTotalCost: null,
    costLayer: null,
    costLayerLoading: false,
    costLayerSaving: false,
  });
  readonly state$ :Observable<ICostLayerEditState> = this.state.$;

  readonly computedTotalCost$ :Observable<string | number> =
    this.state.observe('computedTotalCost');
  readonly costLayer$ :Observable<ICostLayer> = this.state.observe('costLayer');
  readonly costLayerLoading$ :Observable<boolean> =
    this.state.observe('costLayerLoading');
  readonly costLayerSaving$ :Observable<boolean> =
    this.state.observe('costLayerSaving');

  /** Unified loading or saving observable. */
  readonly costLayerLoadingOrSaving$ :Observable<boolean> = this.state.observe(
    (state) => state.costLayerLoading || state.costLayerSaving,
  );

  constructor(private api :CostLayersApiService) {
    super();
    this.init();
  }

  load(
    id :ICostLayer['id'],
    options ?:ICostLayerEditDataStoreLoadOptions,
  ) :Observable<ICostLayer> {
    // fill options with defaults
    options = {
      relations: null,
      ...options,
    };

    // process the options
    const params :HttpParamsHash = {};

    if (options.relations) {
      params.relations = options.relations.join('|');
    }

    // do it
    this.updateState({ costLayerLoading: true });

    return this.api.getById(id, params).pipe(
      finalize(() => this.updateState({ costLayerLoading: false })),
      tap((response :ICostLayer) => {
        this.setLayer(response, { loading: false });
      }),
    );
  }

  save(
    data :ICostLayerEditFields,
    options ?:ICostLayerEditDataStoreSaveOptions,
  ) :Observable<{ data :ICostLayer }> {
    options = {
      ...options,
    };

    const id = this.get('costLayer').id;

    this.updateState({ costLayerSaving: true });

    return this.api.updateCostLayer(id, data).pipe(
      finalize(() => this.updateState({ costLayerSaving: false })),
      tap((response :{ data :ICostLayer }) => {
        this.setLayer(response.data);
      }),
    );
  }

  setLayer(costLayer :ICostLayer | null, options ?:{ loading :boolean }) {
    this.updateState({
      computedTotalCost: costLayer?.totalCost,
      costLayer,
      costLayerLoading: options?.loading ?? false,
    });
  }
}
